import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import PageTransition from '../components/PageTransition';
import { SPONSORS_PAGE, SITE_CONFIG } from '../data';
import './SponsorsPage.css';

// Avatar from initials when no logo
function SponsorAvatar({ name, size = 64 }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
  return (
    <div className="sponsor-avatar" style={{ width: size, height: size }}>
      {initials}
    </div>
  );
}

function SponsorTierSection({ tierKey, label, sponsors, delay = 0 }) {
  if (!sponsors.length) return null;
  return (
    <motion.div
      className={`sponsor-tier sponsor-tier-${tierKey}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay }}
    >
      <div className="sponsor-tier-header">
        <span className="sponsor-tier-label">{label}</span>
      </div>
      <div className={`sponsor-grid sponsor-grid-${tierKey}`}>
        {sponsors.map((s, i) => (
          <motion.a
            key={s.id}
            href={s.website}
            target="_blank"
            rel="noopener noreferrer"
            className="sponsor-card"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: delay + i * 0.07 }}
          >
            <div className="sponsor-card-logo">
              {s.logoUrl
                ? <img src={s.logoUrl} alt={s.name} />
                : <SponsorAvatar name={s.name} size={tierKey === 'platinum' ? 56 : 44} />
              }
            </div>
            <div className="sponsor-card-info">
              <h3>{s.name}</h3>
              <p>{s.description}</p>
            </div>
            <ExternalLink size={14} className="sponsor-external" />
          </motion.a>
        ))}
      </div>
    </motion.div>
  );
}

export default function SponsorsPage() {
  const d = SPONSORS_PAGE;
  const tierOrder = ['Ana', 'Bronz', 'Ürün', 'media'];

  return (
    <PageTransition>
      <div className="sponsors-page">

        <section className="page-hero">
          <div className="page-hero-bg" />
          <div className="container page-hero-content">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="section-tag">{d.hero.tag}</span>
              <h1>{d.hero.title}</h1>
              <p>{d.hero.description}</p>
            </motion.div>
          </div>
        </section>

        <section className="sponsors-main">
          <div className="container">

            {tierOrder.map((tierKey, ti) => {
              const tierSponsors = d.sponsors.filter(s => s.tier === tierKey);
              return (
                <SponsorTierSection
                  key={tierKey}
                  tierKey={tierKey}
                  label={d.tiers[tierKey]}
                  sponsors={tierSponsors}
                  delay={ti * 0.1}
                />
              );
            })}

            {/* Partner CTA */}
            <motion.div
              className="partner-cta"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="partner-cta-orb" />
              <div className="partner-cta-content">
                <h2>{d.partnerTitle}</h2>
                <p>{d.partnerDescription}</p>
                <a href={`mailto:${SITE_CONFIG.email}`} className="btn-primary">
                  {d.partnerBtn}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </a>
              </div>
            </motion.div>

          </div>
        </section>

      </div>
    </PageTransition>
  );
}
